/**
 * List attachments component
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @author Deitric Jang <djang@buildcentrix.com>
 *
 * @copyright (c) 2022-2024 Build Centrix
 */
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { WdMediaUploadService } from '../../services/media/wd-media-upload.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ManageAttachmentsComponent } from '../modals/order-pending-attachments/manage-attachments.component';
import { WdLocalSharedFunctionsService } from '../../services/shared/wd-local-shared-functions.service';
import { WdLocalEnvironmentService } from '../../services/environment/wd-local-environment.service';
import { SelectionModel } from '@angular/cdk/collections';
import { IOrderAttachmentModel } from '../../interfaces/orders/order-interfaces';

@Component({
  selector: 'app-order-attachments',
  templateUrl: './list-attachments.component.html',
  styleUrls: ['./list-attachments.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ListAttachmentsComponent {
  // Inputs
  @Input() orderIndex = '';
  @Input() attachments: Array<IOrderAttachmentModel> = [];

  // Outputs
  @Output() isChanges: EventEmitter<any> = new EventEmitter();

  // Selections
  attachmentsListSelections = new SelectionModel(true, [] as any);

  /**
   * Constructor
   * @since 4.36.0
   */
  constructor(
    private matDialog: MatDialog,
    private wdTranslate: TranslateService,
    public wdEnvironmentService: WdLocalEnvironmentService,
    public wdFileUploadService: WdMediaUploadService,
    @Inject('wdErrorService') public wdErrorService: any,
  ) {}

  /**
   * On Edit File
   * @since 4.36.0
   */
  async onEditFile(file: any): Promise<void> {
    const filesToManage = [file];
    // Make a copy of attachments
    const attachmentsUntouched = JSON.stringify(this.attachments);
    // Open Attachments Dialog
    this.matDialog.open(ManageAttachmentsComponent, {
      disableClose: true,
      width: '800px',
      data: {
        orderIndex: this.orderIndex,
        files: filesToManage,
        onCancelCallback: () => {
          // Revert attachments changes
          this.attachments = JSON.parse(
            attachmentsUntouched,
            WdLocalSharedFunctionsService.revertSectionDateReviver,
          );
        },
        onSuccessCallback: () => {
          // Trigger changes callback
          this.isChanges.emit(this.attachments);
        },
      },
    });
  }

  /**
   * On Remove File
   * @since 4.36.0
   */
  async onRemoveFile(file: any): Promise<void> {
    // Start Loader
    file.isRemoving = true;
    try {
      const response = await this.wdFileUploadService.removeAttachedMediaFile(
        this.orderIndex,
        file.index,
      );
      // Remove file by name
      this.attachments = this.attachments.filter(
        (data: any) => data.index != file.index,
      );
      // Trigger changes callback
      this.isChanges.emit(this.attachments);
      // Success message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        response.statusText,
      );
    } catch (error) {
      // Error message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Stop Loader
      file.isRemoving = false;
    }
  }

  /**
   * Downloads the selected files
   * @since 4.67.0
   */
  downloadMultipleFiles() {
    this.attachmentsListSelections.selected.forEach(
      (attachment: IOrderAttachmentModel) => {
        window.open(attachment.url);
      },
    );
  }

  /**
   * Selects all attachment rows if they are not all selected; otherwise clear selection.
   * @since 4.67.0
   */
  toggleAllCheckboxes(): void {
    this.isAllSelected()
      ? this.attachmentsListSelections.clear()
      : this.attachments.forEach((row) => {
          this.attachmentsListSelections.select(row);
        });
  }

  /**
   * Is called by the view and returns whether the number of
   * selected attachments matches the total number of rows.
   * @since 4.67.0
   */
  isAllSelected(): boolean {
    const numSelected = this.attachmentsListSelections.selected.length;
    const numRows = this.attachments.length;
    return numSelected === numRows;
  }

  /**
   * The label for the checkbox on the passed row
   * @since 4.67.0
   * @memberof ListComponent
   */
  setCheckboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'Deselect' : 'Select'} All Rows`;
    }
    return `${
      this.attachmentsListSelections.isSelected(row) ? 'Deselect' : 'Select'
    } Attachment ${row.name}`;
  }
}
